import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const authApi = {
  me: () => client.get("/auth/me").then((r) => r.data),
  createSession: (sessionId) =>
    client.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
};

export const adminApi = {
  listUsers: () => client.get("/admin/users").then((r) => r.data),
  approve: (userId, role) => client.post(`/admin/users/${userId}/approve`, { role }).then((r) => r.data),
  reject: (userId) => client.post(`/admin/users/${userId}/reject`).then((r) => r.data),
  suspend: (userId) => client.post(`/admin/users/${userId}/suspend`).then((r) => r.data),
  setRole: (userId, role) => client.post(`/admin/users/${userId}/role`, { role }).then((r) => r.data),
  listInvites: () => client.get("/admin/invitations").then((r) => r.data),
  invite: (data) => client.post("/admin/invitations", data).then((r) => r.data),
  resendInvite: (id, appUrl) => client.post(`/admin/invitations/${id}/resend`, null, { params: { app_url: appUrl } }).then((r) => r.data),
  revokeInvite: (id) => client.post(`/admin/invitations/${id}/revoke`).then((r) => r.data),
  logs: () => client.get("/admin/access-logs").then((r) => r.data),
};

export const invitationsApi = {
  get: (token) => client.get(`/invitations/${token}`).then((r) => r.data),
};

export const eventsApi = {
  list: () => client.get("/events").then((r) => r.data),
  get: (id) => client.get(`/events/${id}`).then((r) => r.data),
  create: (data) => client.post("/events", data).then((r) => r.data),
  update: (id, data) => client.put(`/events/${id}`, data).then((r) => r.data),
  remove: (id) => client.delete(`/events/${id}`).then((r) => r.data),
  guests: (id) => client.get(`/events/${id}/guests`).then((r) => r.data),
  addGuest: (id, data) => client.post(`/events/${id}/guests`, data).then((r) => r.data),
  rsvp: (id, data) => client.post(`/events/${id}/rsvp`, data).then((r) => r.data),
  attendees: (id) => client.get(`/events/${id}/attendees`).then((r) => r.data),
  removeGuest: (id, guestId) => client.delete(`/events/${id}/guests/${guestId}`).then((r) => r.data),
  stats: (id) => client.get(`/events/${id}/stats`).then((r) => r.data),
  checkSlug: (params) => client.get("/event-slugs/check", { params }).then((r) => r.data),
  upload: (formData) => client.post(`/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  uploadMedia: (formData) => client.post(`/media/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
};

export const exportApi = {
  fields: (eventId) => client.get(`/events/${eventId}/export/fields`).then((r) => r.data),
  templates: () => client.get("/export-templates").then((r) => r.data),
  saveTemplate: (data) => client.post("/export-templates", data).then((r) => r.data),
  deleteTemplate: (id) => client.delete(`/export-templates/${id}`).then((r) => r.data),
};

export async function downloadGuestExport(eventId, body) {
  const r = await client.post(`/events/${eventId}/export`, body, { responseType: "blob" });
  const match = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = match ? match[1] : `guests.${body.format}`;
  a.click();
  URL.revokeObjectURL(a.href);
  return r.headers["x-export-rows"] || "";
}

export const enquiryApi = {
  create: (data) => client.post(`/enquiries`, data).then((r) => r.data),
};

export const pastWorkApi = {
  listPublic: () => client.get(`/past-work`).then((r) => r.data),
  listAll: () => client.get(`/admin/past-work`).then((r) => r.data),
  create: (data) => client.post(`/past-work`, data).then((r) => r.data),
  update: (id, data) => client.put(`/past-work/${id}`, data).then((r) => r.data),
  remove: (id) => client.delete(`/past-work/${id}`).then((r) => r.data),
};

export const serviceCategoriesApi = {
  listPublic: () => client.get(`/service-categories`).then((r) => r.data),
  listAll: () => client.get(`/admin/service-categories`).then((r) => r.data),
  create: (d) => client.post(`/service-categories`, d).then((r) => r.data),
  update: (id, d) => client.put(`/service-categories/${id}`, d).then((r) => r.data),
  remove: (id) => client.delete(`/service-categories/${id}`).then((r) => r.data),
};

export const vendorsApi = {
  list: (params = {}) => client.get(`/vendors`, { params }).then((r) => r.data),
  get: (id) => client.get(`/vendors/${id}`).then((r) => r.data),
  create: (d) => client.post(`/vendors`, d).then((r) => r.data),
  update: (id, d) => client.put(`/vendors/${id}`, d).then((r) => r.data),
  remove: (id) => client.delete(`/vendors/${id}`).then((r) => r.data),
};

export const servicesApi = {
  listAdmin: (params = {}) => client.get(`/admin/services`, { params }).then((r) => r.data),
  getAdmin: (id) => client.get(`/admin/services/${id}`).then((r) => r.data),
  create: (d) => client.post(`/services`, d).then((r) => r.data),
  update: (id, d) => client.put(`/services/${id}`, d).then((r) => r.data),
  setStatus: (id, status) => client.put(`/services/${id}/status`, null, { params: { status } }).then((r) => r.data),
  duplicate: (id) => client.post(`/services/${id}/duplicate`).then((r) => r.data),
  remove: (id) => client.delete(`/services/${id}`).then((r) => r.data),
};

export const publicServicesApi = {
  list: (params = {}) => client.get(`/public/services`, { params }).then((r) => r.data),
  get: (slug) => client.get(`/public/services/${slug}`).then((r) => r.data),
};

export const albumApi = {
  list: (eventId) => client.get(`/events/${eventId}/album`).then((r) => r.data),
  add: (eventId, formData) => client.post(`/events/${eventId}/album`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  remove: (eventId, itemId) => client.delete(`/events/${eventId}/album/${itemId}`).then((r) => r.data),
};

export const formsApi = {
  list: (eventId) => client.get(`/events/${eventId}/forms`).then((r) => r.data),
  create: (eventId, data) => client.post(`/events/${eventId}/forms`, data).then((r) => r.data),
  get: (formId) => client.get(`/forms/${formId}`).then((r) => r.data),
  update: (formId, data) => client.put(`/forms/${formId}`, data).then((r) => r.data),
  setStatus: (formId, status) => client.put(`/forms/${formId}/status?status=${status}`).then((r) => r.data),
  remove: (formId) => client.delete(`/forms/${formId}`).then((r) => r.data),
  submissions: (formId) => client.get(`/forms/${formId}/submissions`).then((r) => r.data),
  submission: (formId, subId) => client.get(`/forms/${formId}/submissions/${subId}`).then((r) => r.data),
  updateSubmission: (formId, subId, data) => client.put(`/forms/${formId}/submissions/${subId}`, data).then((r) => r.data),
  deleteSubmission: (formId, subId) => client.delete(`/forms/${formId}/submissions/${subId}`).then((r) => r.data),
  documents: (formId) => client.get(`/forms/${formId}/documents`).then((r) => r.data),
};

export const publicFormsApi = {
  get: (eventSlug, formSlug) => client.get(`/public/forms/${eventSlug}/${formSlug}`).then((r) => r.data),
  submit: (formId, data) => client.post(`/public/forms/${formId}/submit`, data).then((r) => r.data),
  upload: (formData, onUploadProgress) =>
    client.post(`/upload`, formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress }).then((r) => r.data),
};

export const documentsApi = {
  forEvent: (eventId) => client.get(`/events/${eventId}/documents`).then((r) => r.data),
  delete: (data) => client.post(`/documents/delete`, data).then((r) => r.data),
};

export async function downloadFile(path, filename, { method = "get", data } = {}) {
  const resp = await client.request({ url: path, method, data, responseType: "blob" });
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const fileUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

export default client;
